// AudioEngine — framework-agnostic live-interview engine. No React.
//
// Owns the WebSocket to /coach/ws/<id> and the two Web Audio contexts:
//   - mic capture at 16 kHz -> AudioWorklet (pcm-worklet) -> Int16 frames -> ws
//   - playback at 24 kHz, gapless scheduling, barge-in on "interrupted"
//
// Hard-won behaviours preserved from the Jinja client (do not "simplify"):
//   - Both AudioContexts are created + resumed INSIDE the user gesture (start()),
//     before any await, or iOS Safari leaves them suspended and no audio flows.
//   - A silent zero frame is sent every 500 ms when the mic is gated, to keep
//     Gemini's server-side inactivity timer from closing the upstream socket.
//   - A JSON {type:"ping"} keepalive every 20 s.
//   - On "interrupted", all queued playback is stopped immediately (barge-in).
//
// The protocol (gemini_live.py docstring) is treated as authoritative.

import { base64ToInt16, pcm16ToFloat32 } from "./audio-math";

export type CoachState =
  | "idle"
  | "connecting"
  | "listening"
  | "speaking"
  | "thinking"
  | "paused"
  | "complete"
  | "error";

export interface TranscriptLine {
  role: "coach" | "candidate";
  text: string;
}

export interface Usage {
  inputTokens: number;
  outputTokens: number;
}

export interface AudioEngineCallbacks {
  onState?: (s: CoachState) => void;
  onTranscript?: (line: TranscriptLine) => void;
  onUsage?: (u: Usage) => void;
  onError?: (message: string) => void;
}

const MIC_RATE = 16000;
const PLAYBACK_RATE = 24000;
const SILENT_FRAME_MS = 500;
const PING_MS = 20000;

export class AudioEngine {
  private ws: WebSocket | null = null;
  private micCtx: AudioContext | null = null;
  private playbackCtx: AudioContext | null = null;
  private workletNode: AudioWorkletNode | null = null;
  private micStream: MediaStream | null = null;

  private nextPlayTime = 0;
  private activeSources = new Set<AudioBufferSourceNode>();

  private silentTimer: ReturnType<typeof setInterval> | null = null;
  private pingTimer: ReturnType<typeof setInterval> | null = null;
  private lastFrameSentAt = 0;

  private state: CoachState = "idle";

  constructor(
    private readonly sessionId: string,
    private readonly cb: AudioEngineCallbacks = {},
  ) {}

  /** Build the same-origin WebSocket URL. Override only in tests. */
  private wsUrl(): string {
    const proto = location.protocol === "https:" ? "wss:" : "ws:";
    return `${proto}//${location.host}/coach/ws/${encodeURIComponent(this.sessionId)}`;
  }

  private setState(s: CoachState) {
    this.state = s;
    this.cb.onState?.(s);
  }

  /** MUST be called from a user gesture (click). Creates + resumes both
   *  AudioContexts synchronously before any await. */
  async start(): Promise<void> {
    this.setState("connecting");

    // 1) Create + resume contexts INSIDE the gesture (iOS requirement).
    this.micCtx = new AudioContext({ sampleRate: MIC_RATE });
    this.playbackCtx = new AudioContext({ sampleRate: PLAYBACK_RATE });
    await Promise.all([this.micCtx.resume(), this.playbackCtx.resume()]);
    this.nextPlayTime = this.playbackCtx.currentTime;

    // 2) Mic capture -> worklet -> Int16 frames. A refused or missing mic used
    // to leave the screen on "Connecting…" forever; surface it instead.
    try {
      this.micStream = await navigator.mediaDevices.getUserMedia({
        audio: {
          sampleRate: MIC_RATE,
          channelCount: 1,
          echoCancellation: true,
          noiseSuppression: true,
        },
      });
    } catch (e) {
      const name = (e as { name?: string })?.name;
      this.cb.onError?.(
        name === "NotAllowedError" || name === "SecurityError"
          ? "Microphone access is blocked. Allow the microphone for this site in your browser settings, then reload the page."
          : "We couldn't find a working microphone. Check one is connected, then reload the page.",
      );
      this.setState("error");
      this.dispose();
      throw e;
    }
    const source = this.micCtx.createMediaStreamSource(this.micStream);
    // Worklet is a plain-JS asset under public/ (see pcm-encoder.js). BASE_URL
    // ("/app/") keeps the path correct under the Flask SPA mount.
    await this.micCtx.audioWorklet.addModule(`${import.meta.env.BASE_URL}worklets/pcm-encoder.js`);
    this.workletNode = new AudioWorkletNode(this.micCtx, "pcm-encoder");
    this.workletNode.port.onmessage = (e: MessageEvent) => {
      const data = e.data as { type: string; pcm: ArrayBuffer };
      if (data.type === "frame") this.sendFrame(data.pcm);
    };
    source.connect(this.workletNode);
    this.workletNode.connect(this.micCtx.destination); // silent (no output written)

    // 3) WebSocket.
    this.openSocket();

    // 4) Keepalives.
    this.silentTimer = setInterval(() => this.maybeSendSilence(), SILENT_FRAME_MS);
    this.pingTimer = setInterval(() => this.send({ type: "ping" }), PING_MS);
  }

  private openSocket() {
    const ws = new WebSocket(this.wsUrl());
    ws.binaryType = "arraybuffer";
    ws.onopen = () => this.setState("listening");
    ws.onmessage = (e) => this.handleMessage(e);
    // ponytail: only set "connection error" if no server error message arrived first via onmessage
    ws.onerror = () => { if (this.state !== "error") this.cb.onError?.("connection error"); };
    ws.onclose = () => {
      if (this.state !== "complete") this.setState("error");
    };
    this.ws = ws;
  }

  private send(obj: Record<string, unknown>) {
    if (this.ws?.readyState === WebSocket.OPEN) this.ws.send(JSON.stringify(obj));
  }

  private sendFrame(buf: ArrayBuffer) {
    if (this.ws?.readyState === WebSocket.OPEN) {
      this.ws.send(buf);
      this.lastFrameSentAt = Date.now();
    }
  }

  /** Send a silent frame if the gate has been closed for a while — keeps the
   *  upstream Gemini socket from timing out during candidate silence. */
  private maybeSendSilence() {
    if (this.state === "paused") return;
    if (Date.now() - this.lastFrameSentAt < SILENT_FRAME_MS) return;
    const silent = new Int16Array(MIC_RATE / 10); // 100 ms of silence
    this.sendFrame(silent.buffer);
  }

  private handleMessage(e: MessageEvent) {
    let msg: { type?: string; [k: string]: unknown };
    try {
      msg = JSON.parse(typeof e.data === "string" ? e.data : "");
    } catch {
      return;
    }
    switch (msg.type) {
      case "ack":
      case "ready":
        this.setState("listening");
        break;
      case "audio":
        this.setState("speaking");
        this.schedulePlayback(String(msg.data ?? ""));
        break;
      case "interrupted":
        this.stopAllAudio();
        this.setState("listening");
        break;
      case "turn_complete":
        if (this.state === "speaking") this.setState("listening");
        break;
      case "transcript_coach":
        this.cb.onTranscript?.({ role: "coach", text: String(msg.text ?? "") });
        break;
      case "usage":
        this.cb.onUsage?.({
          inputTokens: Number(msg.input_tokens ?? 0),
          outputTokens: Number(msg.output_tokens ?? 0),
        });
        break;
      case "session_timeout":
        this.cb.onError?.(String(msg.text ?? "session time limit reached"));
        this.setState("complete");
        this.dispose();
        break;
      case "complete":
        this.setState("complete");
        this.dispose();
        break;
      case "error":
        this.cb.onError?.(String(msg.text ?? "session error"));
        this.setState("error");
        break;
      case "pong":
        break;
    }
  }

  private schedulePlayback(b64: string) {
    if (!this.playbackCtx || !b64) return;
    const pcm = base64ToInt16(b64);
    if (pcm.length === 0) return;
    const float = pcm16ToFloat32(pcm);
    const buffer = this.playbackCtx.createBuffer(1, float.length, PLAYBACK_RATE);
    buffer.getChannelData(0).set(float); // avoids TS typed-array buffer-generic mismatch
    const src = this.playbackCtx.createBufferSource();
    src.buffer = buffer;
    src.connect(this.playbackCtx.destination);
    const at = Math.max(this.playbackCtx.currentTime, this.nextPlayTime);
    src.start(at);
    this.nextPlayTime = at + buffer.duration;
    this.activeSources.add(src);
    src.onended = () => this.activeSources.delete(src);
  }

  private stopAllAudio() {
    for (const src of this.activeSources) {
      try {
        src.stop();
      } catch {
        /* already stopped */
      }
    }
    this.activeSources.clear();
    if (this.playbackCtx) this.nextPlayTime = this.playbackCtx.currentTime;
  }

  // --- Public actions ---

  pause() {
    this.setState("paused");
    void this.micCtx?.suspend();
  }

  resume() {
    void this.micCtx?.resume();
    this.setState("listening");
  }

  nextQuestion() {
    this.setState("thinking");
    this.send({ type: "next_question" });
  }

  end() {
    this.send({ type: "end_session" });
    this.setState("complete");
  }

  dispose() {
    if (this.silentTimer) clearInterval(this.silentTimer);
    if (this.pingTimer) clearInterval(this.pingTimer);
    this.silentTimer = this.pingTimer = null;
    this.stopAllAudio();
    try {
      this.workletNode?.disconnect();
    } catch {
      /* noop */
    }
    this.micStream?.getTracks().forEach((t) => t.stop());
    void this.micCtx?.close();
    void this.playbackCtx?.close();
    this.micCtx = this.playbackCtx = null;
    if (this.ws && this.ws.readyState <= WebSocket.OPEN) this.ws.close();
    this.ws = null;
  }
}
